import { stringify } from 'query-string'
import {
  CreateResult,
  DataProvider,
  DeleteManyResult,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyReferenceResult,
  GetManyResult,
  GetOneResult,
  UpdateManyResult,
  UpdateResult,
} from 'react-admin'
import axios from '../../../config/connections'
import { setFormBodyReq } from '../../../utils/form'
import { kebabCase } from 'lodash'
import { InternalRestResourceOptions, RestResourcesOptions } from '../type'
import config from '../../../config'

const getResourceOptions = (
  resource: string,
  options: RestResourcesOptions,
): InternalRestResourceOptions => {
  const resourceOptions = options[resource]

  if (!resourceOptions) {
    return {
      fullTextSearchFields: [],
      path: null,
    }
  }

  return {
    fullTextSearchFields: resourceOptions.fullTextSearchFields || [],
    path: resourceOptions.path || null,
  }
}
const url = config('mst.rest.url')
const normalizeFilter = ({ filter }: GetListParams, resourceOptions): object => {
  const { fullTextSearchFields } = resourceOptions
  const { q, ...restFilters } = filter
  const newFilter = restFilters

  if (fullTextSearchFields.length === 0) return newFilter

  if (q)
    fullTextSearchFields.forEach((searchField) => {
      newFilter[searchField] = q
    })

  return newFilter
}
const parseResourceToUri = (resource: string, options: RestResourcesOptions): string => {
  return options[resource]?.path || kebabCase(resource)
}
export const mstDataProvider = (resources: RestResourcesOptions): DataProvider => ({
  getList: async (resource: string, params: GetListParams): Promise<GetListResult> => {
    const resourceOptions = getResourceOptions(resource, resources)
    const { page, perPage } = params.pagination
    const { field: sortColumn, order: sortDirection } = params.sort
    const query = {
      p: page,
      limit: perPage,
      sort_direction: sortDirection,
      sort_column: sortColumn,
      ...normalizeFilter(params, resourceOptions),
    }
    const path = `${parseResourceToUri(resource, resources)}?${stringify(query)}`
    const response = await axios.get(`${url}/${path}`)
    const result = response.data.result.map((v) => ({
      id: v?.id ? v?.id : v.code,
      ...v,
    }))

    return {
      data: result,
      total: response.data.total,
    }
  },
  getOne: async (): Promise<GetOneResult> => {
    return {
      data: null,
    }
  },
  getMany: async (resource, { ids }): Promise<GetManyResult> => {
    const query = { id: ids }
    const path = `${parseResourceToUri(resource, resources)}?${stringify(query)}`
    const response = await axios.get(`${url}/${path}`)
    const result = response.data.result.map((v) => ({
      id: v?.id ? v?.id : v.code,
      ...v,
    }))
    return {
      data: result,
    }
  },
  getManyReference: async (resource, params): Promise<GetManyReferenceResult> => {
    const { page, perPage } = params.pagination
    const { field, order } = params.sort
    const query = {
      sort: JSON.stringify([field, order]),
      range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      filter: JSON.stringify({
        ...params.filter,
        [params.target]: params.id,
      }),
    }
    const path = `${parseResourceToUri(resource, resources)}?${stringify(query)}`
    const response = await axios.get(`${url}/${path}`)
    const result = response.data.result.map((v) => ({
      id: v?.id ? v?.id : v.code,
      ...v,
    }))
    return {
      data: result,
    }
  },
  create: async (resource, { data }): Promise<CreateResult> => {
    const { encType } = setFormBodyReq(data)
    const path = parseResourceToUri(resource, resources)
    const { data: resData } = await axios.post(`${url}/${path}`, data, {
      headers: {
        'Content-Type': encType,
      },
    })

    return {
      data: { ...resData.result, id: resData.result.inserted_id },
    }
  },
  update: async (): Promise<UpdateResult> => {
    return {
      data: null,
    }
  },
  updateMany: async (resource, { ids, data }): Promise<UpdateManyResult> => {
    const { metaPath, ...payload } = data
    const basePath = metaPath || parseResourceToUri(resource, resources)
    const { encType, body } = setFormBodyReq(payload)
    await Promise.all(
      ids.map((id) =>
        axios.put(`${url}/${basePath}/${id}`, body, {
          headers: {
            'Content-Type': encType,
          },
        }),
      ),
    )
    return {
      data: ids,
    }
  },
  delete: async (): Promise<DeleteResult> => {
    return {
      data: null,
    }
  },
  deleteMany: async (): Promise<DeleteManyResult> => {
    return {
      data: null,
    }
  },
})
